import { useMemo } from 'react'
import {
  Schedule,
  ScheduleRule,
  ScheduleOverride,
  ResolvedDay,
  CutoffWindow,
  TimeSource,
  GlobalScheduleDefaults,
  DayOfWeek,
  OverrideType,
} from '../types'
import { toISODate } from '../utils'

/**
 * Given a date and an anchor date, returns the 1-indexed cycle week number.
 */
export function getCycleWeek(
  dateStr: string,
  anchorDateStr: string,
  cycleLengthWeeks: number,
): number {
  const date = new Date(dateStr + 'T00:00:00')
  const anchor = new Date(anchorDateStr + 'T00:00:00')
  const diffMs = date.getTime() - anchor.getTime()
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))
  const diffWeeks = Math.floor(diffDays / 7)
  const mod =
    ((diffWeeks % cycleLengthWeeks) + cycleLengthWeeks) % cycleLengthWeeks

  return mod + 1
}

/**
 * Converts a JS Date's getDay() (0=Sun..6=Sat) to our DayOfWeek enum (0=Mon..6=Sun).
 */
export function toDayOfWeek(jsDay: number): DayOfWeek {
  return jsDay === 0 ? DayOfWeek.Sunday : ((jsDay - 1) as DayOfWeek)
}

/**
 * Resolves cutoff using 4-tier cascade: override > rule > schedule > global.
 */
export function resolveCutoff(
  globalDefaults: GlobalScheduleDefaults,
  schedule: Schedule,
  rule?: ScheduleRule,
  override?: ScheduleOverride,
): { cutoff: CutoffWindow | null; source: TimeSource | null } {
  // Override level
  if (override?.cutoffDaysBefore != null && override?.cutoffTime != null) {
    return {
      cutoff: {
        cutoffDaysBefore: override.cutoffDaysBefore,
        cutoffTime: override.cutoffTime,
      },
      source: 'override',
    }
  }
  // Rule level
  if (rule?.cutoffDaysBefore != null && rule?.cutoffTime != null) {
    return {
      cutoff: {
        cutoffDaysBefore: rule.cutoffDaysBefore,
        cutoffTime: rule.cutoffTime,
      },
      source: 'rule',
    }
  }
  // Schedule level
  if (
    schedule.defaultCutoffDaysBefore != null &&
    schedule.defaultCutoffTime != null
  ) {
    return {
      cutoff: {
        cutoffDaysBefore: schedule.defaultCutoffDaysBefore,
        cutoffTime: schedule.defaultCutoffTime,
      },
      source: 'schedule',
    }
  }
  // Global level
  if (
    globalDefaults.cutoffDaysBefore != null &&
    globalDefaults.cutoffTime != null
  ) {
    return {
      cutoff: {
        cutoffDaysBefore: globalDefaults.cutoffDaysBefore,
        cutoffTime: globalDefaults.cutoffTime,
      },
      source: 'global',
    }
  }

  return { cutoff: null, source: null }
}

/**
 * Resolves dropoff time using 4-tier cascade: override > rule > schedule > global.
 */
export function resolveDropoff(
  globalDefaults: GlobalScheduleDefaults,
  schedule: Schedule,
  rule?: ScheduleRule,
  override?: ScheduleOverride,
): { dropoffTime: number | null; source: TimeSource | null } {
  if (override?.dropoffTime != null) {
    return { dropoffTime: override.dropoffTime, source: 'override' }
  }
  if (rule?.dropoffTime != null) {
    return { dropoffTime: rule.dropoffTime, source: 'rule' }
  }
  if (schedule.defaultDropoffTime != null) {
    return { dropoffTime: schedule.defaultDropoffTime, source: 'schedule' }
  }
  if (globalDefaults.dropoffTime != null) {
    return { dropoffTime: globalDefaults.dropoffTime, source: 'global' }
  }

  return { dropoffTime: null, source: null }
}

/**
 * Finds the schedule that covers a given date from a sorted list of schedules.
 */
export function findScheduleForDate(
  dateStr: string,
  schedules: Schedule[],
): Schedule | null {
  for (const sched of schedules) {
    if (
      dateStr >= sched.effectiveFrom &&
      (sched.effectiveUntil === null || dateStr <= sched.effectiveUntil)
    ) {
      return sched
    }
  }

  return null
}

/**
 * Resolves all service days for a given year across multiple schedules.
 */
export function resolveYear(
  year: number,
  globalDefaults: GlobalScheduleDefaults,
  schedules: Schedule[],
  rulesBySchedule: Map<string, ScheduleRule[]>,
  overridesBySchedule: Map<string, ScheduleOverride[]>,
): Map<string, ResolvedDay> {
  const result = new Map<string, ResolvedDay>()

  // Collect all overrides indexed by date for fast lookup
  const overridesByDate = new Map<
    string,
    { override: ScheduleOverride; scheduleId: string }
  >()
  for (const [schedId, overrides] of overridesBySchedule) {
    for (const o of overrides) {
      overridesByDate.set(o.date, { override: o, scheduleId: schedId })
    }
  }

  const startDate = new Date(year, 0, 1)
  const endDate = new Date(year, 11, 31)

  for (let d = new Date(startDate); d <= endDate; d.setDate(d.getDate() + 1)) {
    const dateStr = toISODate(d)
    const dayOfWeek = toDayOfWeek(d.getDay())
    const overrideEntry = overridesByDate.get(dateStr)
    const override = overrideEntry?.override

    // Find which schedule covers this date
    const schedule = findScheduleForDate(dateStr, schedules)

    if (!schedule) {
      // No schedule covers this date — check for an Add override anyway
      if (override && override.overrideType === OverrideType.Add) {
        // We need *some* schedule to resolve cascade — use the override's parent schedule
        const parentSched = schedules.find((s) => s.id === override.scheduleId)
        if (parentSched) {
          const { cutoff, source: cutoffSrc } = resolveCutoff(
            globalDefaults,
            parentSched,
            undefined,
            override,
          )
          const { dropoffTime, source: dropoffSrc } = resolveDropoff(
            globalDefaults,
            parentSched,
            undefined,
            override,
          )
          result.set(dateStr, {
            date: dateStr,
            status: 'ad-hoc-on',
            source: 'override',
            scheduleId: parentSched.id,
            cutoff,
            cutoffSource: cutoffSrc,
            dropoffTime,
            dropoffSource: dropoffSrc,
            override,
          })
          continue
        }
      }
      result.set(dateStr, {
        date: dateStr,
        status: 'no-schedule',
        source: 'none',
        scheduleId: null,
        cutoff: null,
        cutoffSource: null,
        dropoffTime: null,
        dropoffSource: null,
      })
      continue
    }

    const rules = rulesBySchedule.get(schedule.id) || []

    // Find matching rule
    let matchingRule: ScheduleRule | undefined
    if (schedule.cycleLengthWeeks !== null) {
      const cycleWeek = getCycleWeek(
        dateStr,
        schedule.cycleAnchorDate,
        schedule.cycleLengthWeeks,
      )
      matchingRule = rules.find(
        (r) => r.cycleWeek === cycleWeek && r.dayOfWeek === dayOfWeek,
      )
    }

    // Apply override logic
    if (override && override.scheduleId === schedule.id) {
      switch (override.overrideType) {
        case OverrideType.Cancel: {
          result.set(dateStr, {
            date: dateStr,
            status: 'cancelled',
            source: 'override',
            scheduleId: schedule.id,
            cutoff: null,
            cutoffSource: null,
            dropoffTime: null,
            dropoffSource: null,
            rule: matchingRule,
            override,
          })
          continue
        }
        case OverrideType.Add: {
          const { cutoff, source: cutoffSrc } = resolveCutoff(
            globalDefaults,
            schedule,
            undefined,
            override,
          )
          const { dropoffTime, source: dropoffSrc } = resolveDropoff(
            globalDefaults,
            schedule,
            undefined,
            override,
          )
          result.set(dateStr, {
            date: dateStr,
            status: 'ad-hoc-on',
            source: 'override',
            scheduleId: schedule.id,
            cutoff,
            cutoffSource: cutoffSrc,
            dropoffTime,
            dropoffSource: dropoffSrc,
            override,
          })
          continue
        }
        case OverrideType.Modify: {
          if (matchingRule) {
            const { cutoff, source: cutoffSrc } = resolveCutoff(
              globalDefaults,
              schedule,
              matchingRule,
              override,
            )
            const { dropoffTime, source: dropoffSrc } = resolveDropoff(
              globalDefaults,
              schedule,
              matchingRule,
              override,
            )
            result.set(dateStr, {
              date: dateStr,
              status: 'service',
              source: 'override',
              scheduleId: schedule.id,
              cutoff,
              cutoffSource: cutoffSrc,
              dropoffTime,
              dropoffSource: dropoffSrc,
              rule: matchingRule,
              override,
            })
          } else {
            result.set(dateStr, {
              date: dateStr,
              status: 'no-schedule',
              source: 'none',
              scheduleId: schedule.id,
              cutoff: null,
              cutoffSource: null,
              dropoffTime: null,
              dropoffSource: null,
              override,
            })
          }
          continue
        }
      }
    }

    // No override — check rule
    if (matchingRule) {
      const { cutoff, source: cutoffSrc } = resolveCutoff(
        globalDefaults,
        schedule,
        matchingRule,
      )
      const { dropoffTime, source: dropoffSrc } = resolveDropoff(
        globalDefaults,
        schedule,
        matchingRule,
      )
      result.set(dateStr, {
        date: dateStr,
        status: 'service',
        source: 'rule',
        scheduleId: schedule.id,
        cutoff,
        cutoffSource: cutoffSrc,
        dropoffTime,
        dropoffSource: dropoffSrc,
        rule: matchingRule,
      })
    } else {
      result.set(dateStr, {
        date: dateStr,
        status: 'no-schedule',
        source: 'none',
        scheduleId: schedule.id,
        cutoff: null,
        cutoffSource: null,
        dropoffTime: null,
        dropoffSource: null,
      })
    }
  }

  return result
}

/**
 * Hook that resolves multiple schedules + rules + overrides into a calendar map for a given year.
 */
export function useCalendarResolver(
  year: number,
  globalDefaults: GlobalScheduleDefaults,
  schedules: Schedule[],
  rulesBySchedule: Map<string, ScheduleRule[]>,
  overridesBySchedule: Map<string, ScheduleOverride[]>,
): Map<string, ResolvedDay> {
  return useMemo(
    () =>
      resolveYear(
        year,
        globalDefaults,
        schedules,
        rulesBySchedule,
        overridesBySchedule,
      ),
    [year, globalDefaults, schedules, rulesBySchedule, overridesBySchedule],
  )
}
